type JSONType =
  | string
  | number
  | boolean
  | null
  | JSONType[]
  | { [key: string]: JSONType }

const httpMethod = <const>{
  DELETE: 'DELETE',
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
}

const baseOpts: RequestInit = {
  credentials: 'same-origin'
}

const jsonOpts: RequestInit = {
  headers: {
    ["Accept"]: "application/json;charset=UTF-8",
  }
}

const postJsonOpts: RequestInit = {
  headers: {
    ["Content-Type"]: "application/json;charset=UTF-8",
  }
}

async function http(request: Request, maxRetryMs = 30000, retryFac = 2, baseRetryMs = 1000) {
  let retryMs = 0,
      attempt = 0,
      errMsg = "",
      stopRetry = false
  while(retryMs < maxRetryMs) {
    try {
      // N.B. We clone the `Request` object here because `fetch`
      // consumes the stream body, making it unusable for retries.
      const resp = await fetch(request.clone())
      const status = resp.status
      switch(status) {
        case 400: // Bad Request
        case 401: // Unauthorized
        case 404: // Not Found
        case 405: // Method Not Allowed
        case 410: // Gone
          stopRetry = true
          throw translateHttpErr(status)
        case 408: // Request Timeout
        case 500: // Internal Server Error
        case 502: // Bad Gateway
        case 503: // Service Unavailable
        case 504: // Gateway Timeout
          throw translateHttpErr(status)
        default:
          return resp
      }
    } catch(err) {
      errMsg = err instanceof Error ? err.message : String(err)
      if (stopRetry) break
    }
    retryMs = Math.min(
      baseRetryMs * Math.pow(retryFac, attempt++),
      maxRetryMs)
    await new Promise(done => { setTimeout(done, retryMs) })
  }
  throw new Error(errMsg)
}

export async function put(uri: string, opts: RequestInit = {}) {
  const method = httpMethod.PUT
  const request = new Request(uri, { method, ...opts, ...baseOpts })
  return await http(request)
}

export async function getJSON(uri: string, opts: RequestInit = {}) {
  const method = httpMethod.GET
  const request  = new Request(uri, { method, ...opts, ...baseOpts, ...jsonOpts })
  return await http(request)
}

export async function postJSON(uri: string, data: JSONType, opts: RequestInit = {}) {
  const method = httpMethod.POST
  const body = JSON.stringify(data)
  const request = new Request(uri, {
    method,
    body,
    ...opts,
    ...baseOpts,
    ...jsonOpts,
    ...postJsonOpts
  })
  return await http(request)
}

export function translateHttpErr(status: number) {
  let msg = ""
  switch(status) {
    case 400: case 405:
      msg = "The application is making invalid requests to a remote service"
      break
    case 401: case 407:
      msg = "You were denied authorization needed to complete this request and may need a login somewhere"
      break
    case 404: case 410:
      msg = "The network location needed to complete this requet cannot be found"
      break
    case 408:
      msg = "The remote service needed to complete this request took an illegal amount of time to respond"
      break
    case 500:
      msg = "Something went wrong on the receiving end of your request"
      break
    case 502:
      msg = "A remote service needed to complete this request failed to respond"
      break
    case 503:
      msg = "A remote service needed to complete this request is too busy or temporarily down for maintenance"
      break
    case 504:
      msg = "A remote service needed to complete this request took too long to respond"
      break
    default:
      msg = "The application was unable to complete a necessary network request"
  }

  return `${msg}. Try again later. Open a ticket with the
  application owners if this continues. Status Code: ${status}`
}
